import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z, ZodIssue } from 'zod';
import moment from 'moment';
import Papa from 'papaparse';
import { useFormikContext } from 'formik';
import { TAssetInvestmentDetailsSchema } from '@brickwise/asset-validation/dist/asset/investmentData';
import { convertToEuropeanNumber, CSVHEADERS, downloadCashflowCsv, parseEuropeanNumber } from 'src/util/cashFlowUtils';
import { LoadingButton } from '@mui/lab';
import CheckIcon from '@mui/icons-material/Check';
import Grid from '@mui/material/Grid';
import { PercentageFieldText } from '../../mui-overrides/Percentage';

interface Props {
  disabled: boolean;
  scenario: 'normal' | 'optimistic' | 'stress';
}
const euNumberFormat = true;

/** Capitalizes the first letter of a string. */
const capitalize = (val: string) => val.charAt(0).toUpperCase() + val.slice(1);

/** Gets the form field name keys based on the scenario. */
const getFieldKeys = (scenario: 'normal' | 'optimistic' | 'stress') => {
  const scenarioSuffix = scenario === 'normal' ? '' : `${capitalize(scenario)}Scenario`;
  return {
    tableKey: `cashFlowTable${scenarioSuffix}` as 'cashFlowTable' | 'cashFlowTableOptimisticScenario' | 'cashFlowTableStressScenario',
    performanceKey: `cashFlowPerformance${scenarioSuffix}` as
      | 'cashFlowPerformance'
      | 'cashFlowPerformanceOptimisticScenario'
      | 'cashFlowPerformanceStressScenario',
  };
};

export const CashflowCsv: FC<Props> = ({ disabled, scenario }) => {
  const [parseError, setParseError] = useState<ZodIssue[] | null>(null);
  const [uploadButtonSuccess, setUploadButtonSuccess] = useState(false);
  const { t } = useTranslation();
  const formik = useFormikContext<TAssetInvestmentDetailsSchema>();
  const { tableKey, performanceKey } = getFieldKeys(scenario);
  const fieldNameBase = 'investmentDetails.';
  const existingCsv = formik.values.investmentDetails?.[tableKey];
  const maturityDate = formik.values.investmentDetails?.maturityDate;
  const hasMaturityDate = formik.values.investmentDetails?.maturity;
  const fixedObservationPeriodInY = formik.values.investorSettings?.fixedObservationPeriodInY;

  const customMessages = {
    maturityDate: t('dashboard.assets.addedit.investmentDetails.cashFlowTable.error.maturityDate'), // if there is a maturity date, the date of the last line of the CSV must match
    fixedObservationPeriodInY: t('dashboard.assets.addedit.investmentDetails.cashFlowTable.error.fixedObservationPeriodInY'),
  };

  const EuropeanNumberSchema = z
    .preprocess((value) => parseEuropeanNumber(value as string, euNumberFormat), z.number())
    .refine((value) => !isNaN(value), { message: t('dashboard.assets.addedit.investmentDetails.cashFlowTable.error.numberFormat') })
    .refine((value) => value >= 0, {
      message: t('dashboard.assets.addedit.investmentDetails.cashFlowTable.error.positiveNumber'),
    });

  const DateSchema = z.preprocess((value) => moment(value as string, 'DD.MM.YYYY', true).toDate(), z.date());
  const DateSchemaLastLine = z.date().refine((value) => {
    const date = moment(value);
    if (!date.isValid()) return false;
    if (hasMaturityDate) {
      return date.isSame(maturityDate);
    }
    return date.isSameOrAfter(moment().add(fixedObservationPeriodInY, 'years'));
  });

  const CSVSchema = {
    date: DateSchema,
    assetValueDevelopment: EuropeanNumberSchema,
    rentalSurplusIndexed: EuropeanNumberSchema,
    loanBalance: EuropeanNumberSchema,
    interest: EuropeanNumberSchema,
    repayment: EuropeanNumberSchema,
    fees: EuropeanNumberSchema,
  };

  const LastLineSchema = {
    date: DateSchemaLastLine,
    assetValueDevelopment: z.number(),
    rentalSurplusIndexed: z.number(),
    loanBalance: z.number(),
    interest: z.number(),
    repayment: z.number(),
    fees: z.number(),
  };

  const CSVGenericSchema = z.object(CSVSchema);
  const CSVLastEntrySchema = z.object(LastLineSchema);

  const ArraySchema = z.array(CSVGenericSchema).refine(
    (array) => {
      try {
        return CSVLastEntrySchema.parse(array[array.length - 1]); // Validate the last element of the array with the LastEntrySchema
      } catch (error) {
        return false;
      }
    },
    {
      message: hasMaturityDate ? customMessages.maturityDate : customMessages.fixedObservationPeriodInY,
    },
  );

  function handleCSVValidation(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event?.target?.files?.[0];
    if (!file) return;
    setParseError(null);
    Papa.parse(file, {
      complete: (results) => {
        try {
          const parsedCSV = ArraySchema.parse(results.data);
          formik.setFieldValue(`${fieldNameBase}${tableKey}`, parsedCSV);
          setUploadButtonSuccess(true);
          // Reset the success button after 2 seconds
          setTimeout(() => {
            setUploadButtonSuccess(false);
          }, 2000);
        } catch (err) {
          if (err instanceof z.ZodError) {
            setParseError(err.issues);
          }
        }
      },
      header: true,
      transformHeader: (header, index) => Object.keys(CSVHEADERS)[index],
    });
  }

  function downloadCSVorTemplate() {
    if (!existingCsv) return downloadCashflowCsv([]);
    const convertedCsv = existingCsv.map((row: any) => {
      const modifiedRow = { ...row };
      delete modifiedRow.distributionSurplus;
      delete modifiedRow.cashFlow;
      for (const [key, value] of Object.entries(modifiedRow)) {
        if (key === 'date') {
          modifiedRow[key] = moment(value as string, 'DD.MM.YYYY', true).format('DD.MM.YYYY');
        } else {
          modifiedRow[key] = convertToEuropeanNumber(value as string, euNumberFormat);
        }
      }
      return modifiedRow;
    });
    downloadCashflowCsv(convertedCsv);
  }

  return (
    <>
      <Typography sx={{ fontWeight: 'bold' }}>{t(`dashboard.assets.addedit.investmentDetails.${tableKey}.scenario`)}</Typography>
      <Grid sx={{ mt: 0, mb: 2 }} container spacing={2} alignItems="center">
        <Grid xs={12} alignItems="center" spacing={2}>
          <Button sx={{ marginRight: 2 }} variant="contained" size="small" onClick={() => downloadCSVorTemplate()}>
            {existingCsv && existingCsv.length > 0
              ? t('dashboard.assets.addedit.investmentDetails.cashFlowTable.download')
              : t('dashboard.assets.addedit.investmentDetails.cashFlowTable.template')}
          </Button>
          <LoadingButton
            variant="contained"
            size="small"
            component="label"
            disabled={disabled}
            loading={uploadButtonSuccess}
            loadingIndicator={<CheckIcon />}
          >
            {t('dashboard.assets.addedit.investmentDetails.cashFlowTable.upload')}
            <input hidden accept="csv/*" type="file" onChange={handleCSVValidation} />
          </LoadingButton>
        </Grid>
        <Grid md={6} xs={12} alignItems="center" sx={{ marginTop: 1 }}>
          <PercentageFieldText
            label={t('dashboard.assets.addedit.investmentDetails.cashFlowTable.performanceField.label')}
            formik={formik}
            disabled={disabled}
            fieldName={`${fieldNameBase}${performanceKey}`}
            hideHelperText
            size="small"
            margin="none"
          />
        </Grid>
      </Grid>
      {parseError &&
        parseError.map((error, index) => (
          <Typography key={index} variant="body1" sx={{ color: 'red' }}>
            - {error.message} {error.path.length > 1 && `Spalte "${error.path[1]}", Zeile ${error.path[0]}`}
          </Typography>
        ))}
    </>
  );
};
